import { useContext, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import Home from "./routes/Home/Home";
import Nav from "./components/Nav/Nav";
import NotFound from "./components/Error/NotFound";
import AuthProvider, { AuthContext } from "./context/AuthProvider";
import ToastProvider from "@/context/ToastProvider";
import Forbidden from "@/components/Error/Forbidden";
import Login from "@/components/Error/Login";
import RealTimeProvider from "@/context/RealTimeProvider";
import SearchResultProvider from "@/context/SearchResultProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CookieModal from "@/components/CookieModal";
import "@skat/bbr-searchcomponent/style.css";

// Lazily loaded so each route's module graph is only fetched when visited,
// instead of every route being pulled into the initial page load.
const EsrSearch = lazy(() => import("./routes/EsrSearch/EsrSearch"));
const Statistik = lazy(() => import("./routes/Statistik/Statistik"));
const Fejllister = lazy(() => import("./routes/Fejllister/Fejllister"));
const Artikler = lazy(() => import("./routes/Artikler/Artikler"));
const Admin = lazy(() => import("@/routes/Admin/Admin"));
const FejllisteID = lazy(() => import("@/routes/Fejllister/FejllisteID"));
const Ejendom = lazy(() => import("@/routes/Ejendom/Ejendom"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const location = useLocation();
  const authState = useContext(AuthContext);

  return (
    <div id="App">
      <Nav authState={authState} />
      <Suspense fallback={<div></div>}>
        <Routes key={location.pathname}>
          <Route
            path="/esrsearch"
            element={
              <PrivateRoute
                requiredRolle="miljoe_bbrexplore_adgang"
                key={location.pathname}
                authState={authState}
              >
                <EsrSearch />
              </PrivateRoute>
            }
          />
          <Route
            path="/fejllister/:id"
            element={
              <PrivateRoute
                requiredRolle="fejllister"
                key={location.pathname}
                authState={authState}
              >
                <FejllisteID />
              </PrivateRoute>
            }
          />
          <Route
            path="/fejllister"
            element={
              <PrivateRoute
                requiredRolle="fejllister"
                key={location.pathname}
                authState={authState}
              >
                <Fejllister />
              </PrivateRoute>
            }
          />
          <Route
            path="/hjaelp-til-fejllister"
            element={
              <PrivateRoute
                requiredRolle="fejllister"
                key={location.pathname}
                authState={authState}
              >
                <Artikler />
              </PrivateRoute>
            }
          />
          <Route
            path="/statistik"
            element={
              <PrivateRoute
                requiredRolle="statistik"
                key={location.pathname}
                authState={authState}
              >
                <Statistik />
              </PrivateRoute>
            }
          />

          <Route
            path="/ejendom"
            element={
              <PrivateRoute
                requiredRolle="miljoe_bbrexplore_adgang"
                key={location.pathname}
                authState={authState}
              >
                <Ejendom />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute
                requiredRolle="administrator"
                key={location.pathname}
                authState={authState}
              >
                <Admin />
              </PrivateRoute>
            }
          />
          <Route
            path="/"
            element={
              <>
                {authState.state === "FORBIDDEN" && <Forbidden />}
                {authState.state === "UNAUTHORIZED" && <Login />}
                {authState.state !== "UNAUTHORIZED" &&
                  authState.state !== "FORBIDDEN" && (
                    <Home authState={authState} />
                  )}
              </>
            }
          />

          <Route element={<NotFound />} />
        </Routes>
      </Suspense>
      <CookieModal />
    </div>
  );
}

const AppWrapper = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <SearchResultProvider>
            <RealTimeProvider>
              <Router>
                <App />
              </Router>
            </RealTimeProvider>
          </SearchResultProvider>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default AppWrapper;
